import { IQuestionnaireSubScore } from '../interfaces/Questionnaire/IQuestionnaireDetailed';
import { QuestionnaireHistoryItem } from '../interfaces/Questionnaire/IQuestionnaireHistory';

export interface QuestionnaireScoreBadge {
  value: number;
  label: string;
  tone: 'green' | 'yellow' | 'orange' | 'red' | 'neutral';
}

export type QuestionnaireScoreState =
  | {
      kind: 'scored';
      value: number;
      label: string;
      tone: QuestionnaireScoreBadge['tone'];
    }
  | {
      // A scale scored in parts with no total, such as the Motricity Index.
      kind: 'subScores';
      subScores: IQuestionnaireSubScore[];
    }
  | {
      kind: 'incomplete';
      label: 'Incomplete';
    }
  | {
      kind: 'none';
    };

export interface QuestionnaireScoreRange {
  min: number;
  max?: number;
  label: string;
  tone: 'green' | 'yellow' | 'orange' | 'red';
}

export function getQuestionnaireScoreBadge(
  item: QuestionnaireHistoryItem,
  _questionnaireTemplateId?: number
): QuestionnaireScoreBadge | null {
  const state = getQuestionnaireScoreState(item);

  if (state.kind !== 'scored') {
    return null;
  }

  return { value: state.value, label: state.label, tone: state.tone };
}

export function getQuestionnaireScoreState(
  item: QuestionnaireHistoryItem,
): QuestionnaireScoreState {
  if (
    item.score !== null &&
    item.score !== undefined &&
    Number.isFinite(Number(item.score))
  ) {
    const tone =
      item.scoreSeverity === 'green' ||
      item.scoreSeverity === 'yellow' ||
      item.scoreSeverity === 'orange' ||
      item.scoreSeverity === 'red'
        ? item.scoreSeverity
        : 'neutral';

    return {
      kind: 'scored',
      value: Number(item.score),
      label: item.scoreLabel ?? 'Recorded score',
      tone,
    };
  }

  if (item.subScores?.length) {
    return {
      kind: 'subScores',
      subScores: item.subScores,
    };
  }

  if (isQuestionnaireScoreIncomplete(item)) {
    return {
      kind: 'incomplete',
      label: 'Incomplete',
    };
  }

  return { kind: 'none' };
}

// Whole scores as they are; a half point, such as a Motricity Index side,
// keeps its one decimal.
export function formatQuestionnaireScoreValue(score: number): string {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

// "Right side 71.5 · Left side 100", in the order the backend sends them.
export function formatQuestionnaireSubScores(
  subScores: IQuestionnaireSubScore[],
  translateLabel: (label: string) => string = (label) => label,
): string {
  return subScores
    .map(
      (subScore) =>
        `${translateLabel(subScore.label)} ${formatQuestionnaireScoreValue(subScore.score)}`,
    )
    .join(' · ');
}

export function isQuestionnaireScoreIncomplete(
  item: QuestionnaireHistoryItem,
): boolean {
  return (
    (item.score === null || item.score === undefined) &&
    typeof item.scoreLabel === 'string' &&
    item.scoreLabel.trim().toLowerCase() === 'incomplete'
  );
}

export function getQuestionnaireScoreRanges(
  questionnaireTemplateId?: number,
  item?: QuestionnaireHistoryItem
): QuestionnaireScoreRange[] {
  if (item?.scoreRanges?.length) {
    return item.scoreRanges.map((range) => ({
      min: Number(range.min),
      max:
        range.max === null || range.max === undefined
          ? undefined
          : Number(range.max),
      label: range.label,
      tone: range.severity,
    }));
  }

  switch (questionnaireTemplateId) {
    case 3:
      return [
        { min: 0, max: 4, label: 'Minor', tone: 'green' },
        { min: 5, max: 15, label: 'Moderate', tone: 'yellow' },
        { min: 16, max: 20, label: 'Moderate to severe', tone: 'orange' },
        { min: 21, label: 'Severe', tone: 'red' },
      ];
    case 5:
      return [
        { min: 0, max: 5, label: 'No depression', tone: 'green' },
        { min: 6, max: 10, label: 'Moderate depression', tone: 'orange' },
        { min: 11, label: 'Severe depression', tone: 'red' },
      ];
    case 6:
      return [
        { min: 0, max: 13, label: 'Minimal', tone: 'green' },
        { min: 14, max: 19, label: 'Mild', tone: 'yellow' },
        { min: 20, max: 28, label: 'Moderate', tone: 'orange' },
        { min: 29, label: 'Severe', tone: 'red' },
      ];
    case 8:
      return [
        { min: 0, max: 20, label: 'High fall risk', tone: 'red' },
        { min: 21, max: 40, label: 'Moderate fall risk', tone: 'orange' },
        { min: 41, label: 'Low fall risk', tone: 'green' },
      ];
    case 12:
      return [
        { min: 0, max: 3, label: 'Neuropathic pain unlikely', tone: 'green' },
        { min: 4, max: 10, label: 'Neuropathic pain likely', tone: 'red' },
      ];
    case 13:
      return [
        { min: 0, max: 33, label: 'Poor self-rated health', tone: 'red' },
        { min: 34, max: 66, label: 'Fair self-rated health', tone: 'yellow' },
        { min: 67, max: 100, label: 'Good self-rated health', tone: 'green' },
      ];
    case 15:
      return [
        { min: 0, max: 0, label: 'No pain', tone: 'green' },
        { min: 1, max: 3, label: 'Mild pain', tone: 'yellow' },
        { min: 4, max: 6, label: 'Moderate pain', tone: 'orange' },
        { min: 7, max: 10, label: 'Severe pain', tone: 'red' },
      ];
    default:
      return [];
  }
}
